export type AlertSeverity = "HIGH" | "MEDIUM" | "LOW";

/** NONE = informational alert, never resolvable. */
export type AlertStatus = "NONE" | "OPEN" | "RESOLVED";

export type AlertRecipientReason = "DIRECT" | "ROLE" | "SUBSCRIPTION";

export type AlertErrorCode =
  | "VALIDATION_ERROR"
  | "NOT_FOUND"
  | "NOT_RESOLVABLE"
  | "ALREADY_RESOLVED"
  | "FORBIDDEN"
  | "CONFLICT";

export interface AlertAdminRef {
  admin_id: number;
  name: string;
}

export interface AlertRef {
  type: string;
  id: string;
  display: string;
}

export interface AlertResolution {
  at: string;
  by: AlertAdminRef;
  note: string;
  source: "MANUAL";
}

export interface AlertItem {
  id: number;
  section: string;
  type_code: string;
  severity: AlertSeverity;
  title: string;
  body: string | null;
  ref: AlertRef;
  parent_ref: { type: string; id: string } | null;
  status: AlertStatus;
  is_resolvable: boolean;
  occurrence_count: number;
  created_at: string;
  last_occurred_at: string;
  me: {
    read_at: string | null;
    archived_at: string | null;
    reason: AlertRecipientReason;
  };
  resolution: AlertResolution | null;
}

export type AlertActivityAction =
  "CREATED" | "READ" | "RESOLVED" | "ARCHIVED" | "UNARCHIVED";

export interface AlertRecipient {
  admin_id: number;
  name: string;
  reason: AlertRecipientReason;
  read_at: string | null;
}

export interface AlertActivity {
  action: AlertActivityAction;
  by: AlertAdminRef | null;
  at: string;
  note: string | null;
}

export interface AlertDetail extends AlertItem {
  payload: Record<string, unknown>;
  recipients: AlertRecipient[];
  activity: AlertActivity[];
}

export interface AlertSectionSummary {
  section: string;
  unread: number;
  high_unread: number;
  open: number;
}

export interface AlertSummary {
  cursor: string | null;
  total_unread: number;
  total_high_unread: number;
  sections: AlertSectionSummary[];
}

export interface AlertMetaSection {
  code: string;
  label: string;
  icon: string | null;
  sort_order: number;
}

export interface AlertMetaSeverity {
  code: AlertSeverity;
  label: string;
  sort_order: number;
}

export interface AlertMetaRefType {
  code: string;
  label: string;
  display_prefix: string;
  section: string;
  web_route: string;
  mobile_route: string;
}

export interface AlertMetaType {
  code: string;
  section: string;
  ref_type: string;
  default_severity: AlertSeverity;
  is_resolvable: boolean;
}

export interface AlertMeta {
  sections: AlertMetaSection[];
  severities: AlertMetaSeverity[];
  ref_types: AlertMetaRefType[];
  alert_types: AlertMetaType[];
}

export type AlertStatusFilter = "ACTIONABLE" | "OPEN" | "RESOLVED" | "ALL";
export type AlertReadFilter = "UNREAD" | "READ" | "ALL";
export type AlertArchivedFilter = "true" | "false" | "all";

export interface AlertListParams {
  section?: string;
  status?: AlertStatusFilter;
  read?: AlertReadFilter;
  severity?: AlertSeverity;
  archived?: AlertArchivedFilter;
  since?: string;
  page_no?: number;
  limit?: number;
}

/** Filter chips shown in the inbox and the navbar panel. */
export type AlertView = "INBOX" | "UNREAD" | "OPEN" | "RESOLVED" | "ARCHIVED";

export const ALERT_VIEW_PARAMS: Record<
  AlertView,
  Pick<AlertListParams, "status" | "read" | "archived">
> = {
  INBOX: { status: "ACTIONABLE", read: "ALL", archived: "false" },
  UNREAD: { status: "ACTIONABLE", read: "UNREAD", archived: "false" },
  OPEN: { status: "OPEN", read: "ALL", archived: "false" },
  RESOLVED: { status: "RESOLVED", read: "ALL", archived: "false" },
  ARCHIVED: { status: "ALL", read: "ALL", archived: "true" },
};

export const ALERT_VIEW_LABELS: Record<AlertView, string> = {
  INBOX: "Inbox",
  UNREAD: "Unread",
  OPEN: "Open",
  RESOLVED: "Resolved",
  ARCHIVED: "Archived",
};

export const ALERT_RESOLVE_NOTE_MIN = 3;
export const ALERT_RESOLVE_NOTE_MAX = 500;
export const ALERT_BULK_MAX_IDS = 200;

/** Badge rule from the API contract. */
export const isAlertUnread = (alert: AlertItem): boolean =>
  alert.me.read_at == null &&
  alert.me.archived_at == null &&
  (alert.status === "OPEN" || alert.status === "NONE");

export const isAlertOpen = (alert: AlertItem): boolean =>
  alert.status === "OPEN" && alert.me.archived_at == null;

export const canResolveAlert = (alert: AlertItem): boolean =>
  alert.is_resolvable && alert.status === "OPEN";
